import { useEffect, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Legacy PERSON+cédula users still `pendiente_documentos`: one-click verify with their stored cédula.
export function LightVerifyModal() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const eligible =
    !!user &&
    user.account_type === "person" &&
    (user.id_type || "cedula") === "cedula" &&
    user.verification_status === "pendiente_documentos";

  // Open once per session per user (unless they dismiss).
  useEffect(() => {
    if (!eligible) { setOpen(false); return; }
    const key = `hittcg_light_verify_dismissed_${user.id}`;
    if (sessionStorage.getItem(key)) { setDismissed(true); return; }
    setOpen(true);
  }, [eligible, user?.id]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/verification/person-light", {});
      toast.success("¡Cuenta verificada! Ya puedes publicar y comprar.");
      setOpen(false);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos verificarte. Revisa tu cédula en Verificación.");
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    if (user?.id) sessionStorage.setItem(`hittcg_light_verify_dismissed_${user.id}`, "1");
    setDismissed(true);
    setOpen(false);
  };

  if (!eligible || dismissed) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent
        data-testid="light-verify-modal"
        className="bg-[#111] border-[#262626] text-white max-w-md"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={20} className="text-emerald-400" />
            <DialogTitle className="font-display text-xl">VERIFICA TU CUENTA</DialogTitle>
          </div>
          <DialogDescription className="text-neutral-400 text-sm leading-relaxed">
Un último paso: confirma tu identidad con la cédula que registraste{user?.cedula ? <> (<span className="font-mono text-white">{user.cedula}</span>)</> : null}. Tu cuenta queda <b className="text-emerald-400">verificada al instante</b> — sin subir fotos.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Button
            data-testid="light-modal-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#EF4444] hover:bg-[#DC2626]"
          >
            <BusyLabel busy={loading} text="Verificando…">Verificar con mi cédula</BusyLabel>
          </Button>
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="light-modal-later"
            className="w-full text-xs text-neutral-500 hover:text-neutral-300 pt-1"
          >
            Más tarde
          </button>
          <div className="text-[11px] text-neutral-500 leading-relaxed pt-2 border-t border-[#262626]">
            🔒 Tus datos se guardan cifrados y solo se usan para confirmar tu identidad. No pedimos fotos ni selfie salvo que haya una disputa activa contigo.
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
